import React from 'react'
import { FileText, X } from 'lucide-react'
import {
  QueryUsecaseTableResult,
  SearchSecuritiesReportChunkResult,
  ShowUsecaseTablesResult,
  QueryRawSqlUsecaseTableResult,
} from 'poc-ai-biz-irdb_domain-model'
import { SourceDocumentQueryUsecaseTableCard, mapToSourceDocumentQueryUsecaseTableCardContent } from './SourceDocumentQueryUsecaseTableCard'
import { SourceDocumentShowUsecaseTableCard, mapToSourceDocumentShowUsecaseTableCardContents } from './SourceDocumentShowUsecaseTableCard'
import {
  SourceDocumentSearchSecuritiesReportChunkCard,
  SourceDocumentSearchSecuritiesReportChunkCardProps,
} from './SourceDocumentSearchSecuritiesReportChunkCard'
import { SourceDocumentQueryRawSqlUsecaseTableCard, mapToSourceDocumentQueryRawSqlUsecaseTableCardContent } from './SourceDocumentQueryRawSqlUsecaseTableCard'

export type SourceDocumentPaneProps = {
  queryUsecaseTableResults: QueryUsecaseTableResult[]
  searchSecuritiesReportChunkResults: SearchSecuritiesReportChunkResult[]
  showUsecaseTablesResults: ShowUsecaseTablesResult[]
  queryRawSqlUsecaseTableResults: QueryRawSqlUsecaseTableResult[]
  expandedQueryUsecaseTableResults: Map<string, QueryUsecaseTableResult>
  expandedShowUsecaseTablesResults: Map<string, ShowUsecaseTablesResult>
  onQueryUsecaseTableClick: (key: string, data: QueryUsecaseTableResult) => void
  onShowUsecaseTableClick: (key: string, data: ShowUsecaseTablesResult) => void
  onQueryRawSqlUsecaseTableClick: (url: string) => void
  onCloseDetail: () => void
}

export const SourceDocumentPane: React.FC<SourceDocumentPaneProps> = ({
  queryUsecaseTableResults,
  searchSecuritiesReportChunkResults,
  showUsecaseTablesResults,
  queryRawSqlUsecaseTableResults,
  expandedQueryUsecaseTableResults,
  expandedShowUsecaseTablesResults,
  onQueryUsecaseTableClick,
  onShowUsecaseTableClick,
  onQueryRawSqlUsecaseTableClick,
  onCloseDetail,
}) => {
  // 一つでも展開されているかの判定
  const isExpanded = expandedQueryUsecaseTableResults.size > 0 || expandedShowUsecaseTablesResults.size > 0

  // showUsecaseTablesResultsから企業ごとの情報を抽出
  const showUsecaseTableCardContents = showUsecaseTablesResults.flatMap((result) =>
    mapToSourceDocumentShowUsecaseTableCardContents(result.usecaseTablesMap, result),
  )
  // queryUsecaseTableResultsをカード表示用に変換
  const queryUsecaseTableCardContents = queryUsecaseTableResults.map((result) => mapToSourceDocumentQueryUsecaseTableCardContent(result))
  // queryRawSqlUsecaseTableResultsをカード表示用に変換
  const queryRawSqlUsecaseTableCardContents = (queryRawSqlUsecaseTableResults || []).map((result) =>
    mapToSourceDocumentQueryRawSqlUsecaseTableCardContent(result),
  )
  // searchSecuritiesReportChunkResultsから全てのチャンクを抽出
  const securitiesReportChunkCards: SourceDocumentSearchSecuritiesReportChunkCardProps[] = searchSecuritiesReportChunkResults.flatMap((res) =>
    res.securitiesReportChunks.map((chunk) => ({ securitiesReportChunk: chunk, query: res.query })),
  )

  return (
    <div className="h-full flex flex-col bg-gray-50">
      <div className={isExpanded ? 'overflow-y-auto border-t' : 'flex-1 overflow-y-auto'}>
        <div className="p-4">
          {/* Company Usecase Tables Cards (from show_usecase_tables) */}
          {showUsecaseTableCardContents.length > 0 && (
            <div className="flex flex-col gap-4 mb-4">
              <div className="flex items-center gap-2">
                {isExpanded && (
                  <button onClick={onCloseDetail}>
                    <X className="h-4 w-4 text-gray-600" />
                  </button>
                )}
                <div className="text-xm font-medium text-gray-600">ユースケーステーブル</div>
              </div>
              <div className="flex flex-col gap-2">
                {showUsecaseTableCardContents.map((cardContent, index) => {
                  const key = cardContent.filerName
                  const isThisExpanded = expandedShowUsecaseTablesResults.has(key)
                  return (
                    <SourceDocumentShowUsecaseTableCard
                      key={index}
                      result={cardContent.result}
                      filerName={cardContent.filerName}
                      tables={cardContent.tables}
                      usecaseTablesMap={cardContent.usecaseTablesMap}
                      totalDataCount={cardContent.totalDataCount}
                      isExpanded={isThisExpanded}
                      onClick={() => onShowUsecaseTableClick(key, cardContent.result)}
                    />
                  )
                })}
              </div>
            </div>
          )}

          {/* Query Usecase Table Cards (from query_usecase_table) */}
          {queryUsecaseTableCardContents.length > 0 && (
            <div className="flex flex-col gap-4 mb-4">
              <div className="flex items-center gap-2">
                {isExpanded && (
                  <button onClick={onCloseDetail}>
                    <X className="h-4 w-4 text-gray-600" />
                  </button>
                )}
              </div>
              <div className="flex flex-col gap-2">
                {queryUsecaseTableCardContents.map((cardContent, index) => {
                  const key = cardContent.result.tableName
                  const isThisExpanded = expandedQueryUsecaseTableResults.has(key)
                  return (
                    <SourceDocumentQueryUsecaseTableCard
                      key={index}
                      result={cardContent.result}
                      displayName={cardContent.displayName}
                      table={cardContent.table}
                      usecaseTablesMap={cardContent.usecaseTablesMap}
                      totalDataCount={cardContent.totalDataCount}
                      isExpanded={isThisExpanded}
                      onClick={() => onQueryUsecaseTableClick(key, cardContent.result)}
                    />
                  )
                })}
              </div>
            </div>
          )}

          {/* Query Raw SQL Usecase Table Cards (from query_raw_sql_usecase_table) */}
          {queryRawSqlUsecaseTableCardContents.length > 0 && (
            <div className="flex flex-col gap-4 mb-4">
              <div className="flex items-center gap-2">
                {isExpanded && (
                  <button onClick={onCloseDetail}>
                    <X className="h-4 w-4 text-gray-600" />
                  </button>
                )}
                <div className="text-xm font-medium text-gray-600">高度なクエリ結果</div>
              </div>
              <div className="flex flex-col gap-2">
                {queryRawSqlUsecaseTableCardContents.map((cardContent, index) => {
                  return (
                    <SourceDocumentQueryRawSqlUsecaseTableCard
                      key={index}
                      result={cardContent.result}
                      displayName={cardContent.displayName}
                      queryDescription={cardContent.queryDescription}
                      spreadsheetSheetUrl={cardContent.spreadsheetSheetUrl}
                      onClick={() => {
                        if (cardContent.spreadsheetSheetUrl) {
                          onQueryRawSqlUsecaseTableClick(cardContent.spreadsheetSheetUrl)
                        }
                      }}
                    />
                  )
                })}
              </div>
            </div>
          )}

          {/* Securities Report Chunk Cards */}
          {securitiesReportChunkCards.length > 0 && (
            <div>
              <div className="text-xm font-medium text-gray-600 mb-2">有価証券報告書</div>
              <div className="flex flex-col gap-2">
                {securitiesReportChunkCards.map((chunk, chunkIndex) => (
                  <SourceDocumentSearchSecuritiesReportChunkCard
                    key={`${chunkIndex}`}
                    securitiesReportChunk={chunk.securitiesReportChunk}
                    query={chunk.query}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
